import React from 'react';
import { action, computed, observable } from 'mobx';
import { get, uniqBy } from 'lodash';

import api from 'core/util/api';
import { setAccountDeviceCount } from 'core/util/uiMetrics';
import IncompleteDeviceCollection from 'app/stores/device/IncompleteDeviceCollection';
import NonCloudDeviceCollection from 'app/stores/device/NonCloudDeviceCollection';
import DeviceTableCollection from 'app/stores/device/DeviceTableCollection';
import DeviceSubtypeIcon, { deviceSubTypeIconMap } from 'app/components/device/DeviceSubtypeIcon';
import { getDeviceTypeOptions } from 'app/util/devices';

import { isGoogleCloud } from '@kentik/ui-shared/util/map';
import DeviceWarningsCollection from 'app/stores/device/DeviceWarningsCollection';
import { modelFinder } from 'app/stores/util/modelFinder';
import { deviceIndex } from 'app/util/lookups/deviceIndex';
import { CLOUD_TYPE_DEVICES } from 'app/util/constants';
import DeviceCacheCollection from './DeviceCacheCollection';
import DeviceCollection from './DeviceCollection';
import DeviceModel from './DeviceModel';
import InterfaceCollection from '../interface/InterfaceCollection';

const buildDeviceWithInterfaces = (attributes) => {
  const device = new DeviceModel();
  device.set(device.deserialize(attributes));

  const { all_interfaces, device_name, site_id, site_name } = attributes;
  const allInterfaces = all_interfaces?.map((intf) => ({ ...intf, device_name, site_id, site_name }));
  const interfaces = new InterfaceCollection(allInterfaces, {
    parent: device,
    sortState: { field: 'interface_description', direction: 'asc' }
  });
  device.set('interfaceCollection', interfaces);

  if (all_interfaces?.length > 0) {
    // save counts so they don't change after filtering
    device.set('interface_count', interfaces.unfilteredSize);
    device.set('down_interface_count', interfaces.downInterfaces.length);

    interfaces.fetchInterfaceFlow(device);
  }

  return device;
};

export class DeviceStore {
  @observable
  fetchFlowCount = 0;

  collection = new DeviceCollection();

  deviceCache = new DeviceCacheCollection(() => this.store.$globalFilters.scopeGeneration);

  tableCollection = new DeviceTableCollection();

  nonCloudCollection = new NonCloudDeviceCollection();

  incompleteDevices = new IncompleteDeviceCollection();

  deviceWarnings = new DeviceWarningsCollection();

  @observable.ref
  deviceSummaries = [];

  /**
   * Device statistics including both company-wide and RBAC-scoped user counts.
   * Always includes both sets of statistics from GET /api/ui/devices/statistics
   * @see fetchDeviceStatistics
   */
  @observable.ref
  deviceStatistics = {
    // Company-wide counts (all devices)
    active_device_count: 0,
    total_device_count: 0,
    non_cloud_device_count: 0,
    cloud_device_count: 0,
    non_nms_device_count: 0,
    has_bgp_neighbor_device: false,
    has_nms_device: false,
    cloud_providers: [],
    device_subtype_counts: [],
    cloud_device_counts: [],
    // RBAC-scoped user counts (respects label restrictions)
    user_active_device_count: 0,
    user_device_subtype_counts: [],
    user_nms_device_count: 0
  };

  // false until the first successful statistics fetch, so consumers can tell "not loaded yet" from "none exist"
  @observable
  hasLoadedDeviceStatistics = false;

  // has received flow for ANYTHING, including pseudo-devices (synthetics). generally you don't want this.
  @observable
  hasReceivedAnyFlow = false;

  // has received flow specifically for devices
  @observable
  hasReceivedFlow = false;

  @observable
  masterBGPDevices = [];

  @observable.ref
  deviceStatusById = {};

  @observable.ref
  initializePromise;

  scopeChangeDisposer = null;

  deviceCacheScopeChangeDisposer = null;

  @action
  initialize() {
    // register collections once.
    this.scopeChangeDisposer ??= this.store.$globalFilters.registerCollections([
      this.collection,
      this.tableCollection,
      this.nonCloudCollection
    ]);

    // deviceCache is lazy and on demand, so scope changes clear it rather than refetch it.
    this.deviceCacheScopeChangeDisposer ??= this.store.$globalFilters.registerScopeChangeHandler(() => {
      this.deviceCache.reset();
    });

    if (!this.initializePromise) {
      if (this.store.$auth.getActiveUserProperty('company.company_status') === 'V') {
        this.initializePromise = Promise.all([
          this.collection.fetch(),
          this.fetchFlowHeartbeat(),
          this.fetchDeviceStatistics().catch((error) => {
            console.error('error fetching device statistics', error);
          })
        ]).then(([devices, hasFlow]) => {
          this.constructDeviceSummaries();
          // start checking for flow every 60 seconds if they have devices, but we haven't received flow yet.
          if (hasFlow === false && devices?.length) {
            this.flowHeartbeatInterval = setInterval(this.fetchFlowHeartbeat, 60000);
          }
        });
      }
    }
    return this.initializePromise || Promise.resolve();
  }

  // device lookups are permission-scoped to the session, so the next user must not see them
  destroy() {
    deviceIndex.remove();
  }

  /**
   * This is only private to make test mocks easier.
   * @returns {(name: string) => import('./DeviceModel').default | undefined}
   * @private
   */
  @computed
  get _byName() {
    return modelFinder(this.collection, 'device_name');
  }

  /**
   * @param {string} device_name
   * @returns {import('app/stores/device/DeviceModel').DeviceModel | undefined}
   */
  byName(device_name) {
    return this._byName(device_name);
  }

  @action
  navigateToDevice = (deviceName) => {
    this.history.push(`/v4/core/quick-views/devices/${deviceName}`);
  };

  @action
  fetchDevice = (deviceName) =>
    api.get(`/api/ui/devices/details/${deviceName}`).then((attributes) => buildDeviceWithInterfaces(attributes));

  // /api/ui/devices/:deviceId and /api/ui/devices/details/:deviceName point to the same findDevice handler
  @action
  fetchDeviceById = (deviceId) =>
    api.get(`/api/ui/devices/${deviceId}`).then((attributes) => buildDeviceWithInterfaces(attributes));

  /**
   * Fetches device statistics, including both company-wide and RBAC-scoped user counts.
   * Always returns both sets of statistics efficiently in a single call.
   *
   * Company-wide stats (all devices):
   *   - active_device_count, cloud_device_count, device_subtype_counts, etc.
   *
   * RBAC-scoped user stats (only devices the user has access to):
   *   - user_active_device_count, user_cloud_device_count, user_device_subtype_counts, etc.
   *
   * @returns {PromiseLike<DeviceStatistics>}
   */
  @action
  fetchDeviceStatistics = () =>
    api.get('/api/ui/devices/statistics').then((response) => {
      this.deviceStatistics = response;
      this.hasLoadedDeviceStatistics = true;
      this.updateDeviceSubtypeFilterOptions();
      setAccountDeviceCount(response.total_device_count);
      return response;
    });

  /**
   * Lightweight bulk lookup of device name + NMS status for a set of device ids. Prefer this over
   * deviceSummariesById when only these two fields are needed (e.g. decorating rows keyed by device id).
   *
   * Hits the recon route (not /api/ui/devices/status) because nms_status is backed by the ReconDevice
   * model (mn_kmetrics_device), a separate table/RBAC scope (recon.enabled) from the generic device
   * route's device_status, which reads from the flow-collector device model.
   * @param {Array<string | number>} ids
   * @returns {PromiseLike<Record<string, {id: string, device_name: string, nms_status: string}>>}
   */
  @action
  fetchDeviceStatusesById = (ids = []) => {
    if (!ids.length) {
      return Promise.resolve({});
    }

    return api
      .get('/api/ui/recon/devices/status', { query: { ids: ids.join(',') } })
      .then((results) =>
        results.reduce((byId, deviceStatus) => {
          byId[deviceStatus.id] = deviceStatus;
          return byId;
        }, {})
      )
      .catch((err) => {
        console.error(err);
        return {};
      });
  };

  /**
   * @returns {Record<string, {device_name: string, site_name: string, site_id: string} | undefined>}
   */
  @computed({ keepAlive: true })
  get deviceSummariesById() {
    const deviceSummariesById = {};
    this.deviceSummaries.forEach((deviceSummary) => {
      deviceSummariesById[deviceSummary.id] = deviceSummary;
    });
    return deviceSummariesById;
  }

  @computed({ keepAlive: true })
  get deviceSummariesByName() {
    const deviceSummariesByName = {};
    this.deviceSummaries.forEach((deviceSummary) => {
      deviceSummariesByName[deviceSummary.device_name] = deviceSummary;
    });
    return deviceSummariesByName;
  }

  /**
   * Resolves a single device summary, preferring an id lookup when both id and name are provided.
   * Centralizes the id-vs-name fallback used by callers that may only have one or the other
   * (e.g. route params that supply either a device id or a device name, but not both).
   * @param {{id?: string, name?: string}} params
   * @returns {{device_name: string, site_name: string, site_id: string} | undefined}
   */
  getDeviceSummary({ id, name }) {
    return (id && this.deviceSummariesById[id]) || (name && this.deviceSummariesByName[name]) || undefined;
  }

  @computed({ keepAlive: true })
  get activeDeviceSummariesById() {
    const deviceSummariesById = {};
    this.activeDeviceSummaries.forEach((deviceSummary) => {
      deviceSummariesById[deviceSummary.id] = deviceSummary;
    });
    return deviceSummariesById;
  }

  @computed({ keepAlive: true })
  get activeDeviceSummariesByName() {
    const deviceSummariesByName = {};
    this.activeDeviceSummaries.forEach((deviceSummary) => {
      deviceSummariesByName[deviceSummary.device_name] = deviceSummary;
    });
    return deviceSummariesByName;
  }

  @computed
  get companySettings() {
    return {
      flow_ips: this.store.$companySettings.flow_ips,
      port: this.store.$companySettings.port
    };
  }

  @computed
  get routerSettings() {
    return {
      snmpPollingIps: this.store.$dictionary.get('general_router_settings.router_snmp_polling_ips')
    };
  }

  @computed
  get activeDeviceSummaries() {
    return this.deviceSummaries.filter((device) => device.device_status === 'V');
  }

  @computed
  get nmsDeviceSummaries() {
    return this.activeDeviceSummaries.filter((device) => device.has_nms_record);
  }

  @computed
  get hasNmsDevices() {
    // Deliberately RBAC-scoped (via user_nms_device_count, which respects label restrictions):
    // this gates access to actual NMS features/navigation for the current user
    // (see $metrics.hasNmsDevices), so it must reflect what this user can see, not the whole company.
    // For a company-wide "has the company enrolled ANY NMS device" boolean (e.g. onboarding checklists),
    // use hasAnyNmsDevice instead.
    return (
      this.store.$auth.hasPermission('recon.enabled', { overrideForSudo: false }) &&
      this.deviceStatistics.user_nms_device_count > 0
    );
  }

  // builds the { label, value } from deviceSummaries instead of the whole collection
  // assuming whenever we show options, we only want options associated with active devices
  @computed
  get deviceSummaryOptions() {
    return this.activeDeviceSummaries.map(
      ({ device_name, id, device_type, device_subtype, ranger_icmp_ip, ranger_snmp_ip, sending_ips }) => ({
        value: device_name,
        label: device_name,
        icon: <DeviceSubtypeIcon type={device_type} />,
        id,
        device_type,
        device_subtype,
        ranger_icmp_ip,
        ranger_snmp_ip,
        sending_ips
      })
    );
  }

  @computed
  get deviceSummariesByType() {
    return this.activeDeviceSummaries.reduce((acc, summary) => {
      const { device_type, device_subtype } = summary;

      if (!acc[device_type]) {
        acc[device_type] = [];
      }
      acc[device_type].push(summary);

      // If they're the same, we don't want to double add!
      if (device_type !== device_subtype) {
        if (!acc[device_subtype]) {
          acc[device_subtype] = [];
        }
        acc[device_subtype].push(summary);
      }
      return acc;
    }, {});
  }

  /**
   * RBAC-scoped (and MKP-scoped) active device counts keyed by device subtype, from statistics,
   * e.g. { router: 8, kappa: 1 }.
   * @returns {Record<string, number>}
   */
  @computed
  get userDeviceSubtypeCounts() {
    const subtypeCounts = this.deviceStatistics.user_device_subtype_counts || [];
    return subtypeCounts.reduce((acc, { device_subtype, count }) => {
      acc[device_subtype] = count;
      return acc;
    }, {});
  }

  /**
   * @returns {string[]}
   */
  @computed
  get uniqueSubtypes() {
    const subtypeCounts = this.deviceStatistics.user_device_subtype_counts || [];
    return subtypeCounts.map(({ device_subtype }) => device_subtype);
  }

  @computed
  get hasDnsProbe() {
    return this.hasSubtype('kprobe');
  }

  @computed
  get hasActiveDevices() {
    return this.deviceStatistics.active_device_count > 0;
  }

  // prefer hasActiveDevices for onboarding/empty-state checks; hasAnyDevices includes devices in ANY
  // status (e.g. deleted), matching the previous, unscoped deviceSummaries.length > 0 semantics.
  @computed
  get hasAnyDevices() {
    return this.deviceStatistics.total_device_count > 0;
  }

  hasSubtype(subtype) {
    return this.deviceStatistics.device_subtype_counts.some(
      (subtypeCount) => subtypeCount.device_subtype === subtype && subtypeCount.count > 0
    );
  }

  @computed
  get hasKappaDevice() {
    return this.hasSubtype('kappa');
  }

  /**
   * RBAC-scoped (and MKP-scoped): whether the user can see at least one active device of this subtype.
   * Returns false until statistics load; check hasLoadedDeviceStatistics to distinguish loading from absence.
   * @param {string} subtype device subtype, e.g. 'aws_subnet'
   * @returns {boolean}
   */
  hasUserSubtype(subtype) {
    return (this.deviceStatistics.user_device_subtype_counts || []).some(
      (subtypeCount) => subtypeCount.device_subtype === subtype && subtypeCount.count > 0
    );
  }

  @computed
  get hasGCEDevice() {
    return this.deviceStatistics.cloud_providers.some(isGoogleCloud);
  }

  @computed
  get hasAWSDevice() {
    return this.deviceStatistics.cloud_providers.includes('aws');
  }

  @computed
  get hasAzureDevice() {
    return this.deviceStatistics.cloud_providers.includes('azure');
  }

  @computed
  get hasOCIDevice() {
    return this.deviceStatistics.cloud_providers.includes('oci');
  }

  @computed
  get nonCloudDeviceCount() {
    return this.deviceStatistics.non_cloud_device_count;
  }

  @computed
  get nonNMSDeviceCount() {
    // Company-wide count (unscoped by this user's RBAC label restrictions) of active devices sending flow
    // (i.e. that have at least one sending_ip), matching the previous activeDeviceSummaries-based semantics.
    return this.deviceStatistics.non_nms_device_count;
  }

  @computed
  get hasBgpNeighborDevice() {
    // Company-wide: true if any active device has its own BGP neighbor IP configured. Narrower than
    // "has a device with BGP configured" (device_bgp_type !== 'none'), since device_bgp_type = 'other_device'
    // peers via another device's session and has no neighbor ip of its own.
    return this.deviceStatistics.has_bgp_neighbor_device;
  }

  @computed
  get hasAnyNmsDevice() {
    // Company-wide: true if the company has ANY active device enrolled in NMS/recon, regardless of this
    // user's RBAC label restrictions. Use this (not hasNmsDevices) for onboarding/empty-state checks that
    // shouldn't vary by what devices the current user happens to be able to see (e.g. SetupTaskModel).
    return (
      this.store.$auth.hasPermission('recon.enabled', { overrideForSudo: false }) &&
      this.deviceStatistics.has_nms_device
    );
  }

  @computed
  get cloudDeviceCount() {
    return this.deviceStatistics.cloud_device_count;
  }

  @computed
  get cloudProviders() {
    return this.deviceStatistics.cloud_providers;
  }

  getCloudDeviceCount(provider, planId) {
    return this.deviceStatistics.cloud_device_counts.reduce((total, cloudDeviceCount) => {
      const matches =
        cloudDeviceCount.cloud_provider === provider && (planId === undefined || cloudDeviceCount.plan_id === planId);
      return matches ? total + cloudDeviceCount.count : total;
    }, 0);
  }

  /**
   * Populate this.deviceSummaries and resulting data. Historically this was a separate call but it was largely just
   * duplicating data from our Device Collections API so it was removed.
   * TODO In future we would like to remove this.deviceSummaries and feed everything from Device Collections.
   * TODO Resulting data should probably be @computed rather than directly constructed
   */
  @action
  loadDeviceSummaries = () =>
    Promise.all([
      this.collection.fetch({ force: true }),
      // keep deviceStatistics (e.g. hasActiveDevices/hasAnyDevices) in sync with device mutations (create/archive/destroy) that
      // refresh deviceSummaries via this method, so callers don't see stale counts until the next reload.
      this.fetchDeviceStatistics().catch((error) => {
        console.error('error fetching device statistics', error);
      })
    ]).then(() => this.constructDeviceSummaries());

  /**
   * Populate this.deviceSummaries and resulting data. Historically this was a separate call but it was largely just
   * duplicating data from our Device Collections API so it was removed.
   * TODO In future we would like to remove this.deviceSummaries and feed everything from Device Collections.
   * TODO Resulting data should probably be @computed rather than directly constructed
   */
  @action
  constructDeviceSummaries() {
    // TODO would be nice to avoid cloning the collection
    this.deviceSummaries = [...this.collection.toJS()];

    return this.deviceSummaries;
  }

  // driven by statistics (RBAC-scoped user_device_subtype_counts)
  @action
  updateDeviceSubtypeFilterOptions() {
    this.store.$dictionary.addFilterFieldValueOptions({
      i_device_subtype: getDeviceTypeOptions(this.uniqueSubtypes)
    });
  }

  @action
  loadDeviceDetails(deviceId) {
    return api.get(`/api/ui/devices/${deviceId}`).then((response) => {
      this.collection.selected.set(response);
      return this.collection.selected;
    });
  }

  // does this Company have any flow at all?
  @action
  fetchFlowHeartbeat = () =>
    api.get('/api/ui/devices/flowHeartbeat').then((result) => {
      this.hasReceivedAnyFlow = !!result?.has_flow;
      this.hasReceivedFlow = !!result?.has_device_flow;

      if (this.hasReceivedFlow) {
        clearInterval(this.flowHeartbeatInterval);
      }

      return result;
    });

  fetchMasterBGPDevices() {
    return api.get('/api/ui/devices/getMasterBgpDevicesList').then((response) => {
      this.masterBGPDevices = response.map((device) => ({
        label: device.device_name,
        value: device.id
      }));
    });
  }

  fetchBgpIngestIps(deviceId) {
    return api.get(`/api/ui/companySettings/getBgpIngestIps/${deviceId || ''}`);
  }

  fetchAllDeviceStatus() {
    return api.get('/api/ui/devices/status');
  }

  fetchDevicesTable({ pagination, filters, sorting } = {}) {
    return api.post('/api/ui/devices/table', {
      data: {
        pagination: { limit: 10, offset: 0, ...pagination },
        filters,
        sorting
      }
    });
  }

  loadNonCloudStatus() {
    return api.get('/api/ui/devices/non-cloud-status').then((response) => {
      this.deviceStatusById = response;

      return response;
    });
  }

  loadDeviceStatus(deviceId) {
    return api.get(`/api/ui/devices/status/${deviceId}`).then((response) => response[deviceId]);
  }

  getDeviceSummaryOptionByName({ deviceName, activeDevicesOnly = true }) {
    const deviceSummaryOption = this.deviceSummaryOptions.find((t) => t.value === deviceName);

    if (activeDevicesOnly) {
      return deviceSummaryOption;
    }

    const fallback = {
      value: deviceName,
      label: `${deviceName} (removed)`
    };

    return deviceSummaryOption || fallback;
  }

  getDeviceSummaryByName({ deviceName, activeDevicesOnly = true }) {
    const deviceSummary = this.deviceSummariesByName[deviceName];
    if (activeDevicesOnly) {
      return deviceSummary;
    }
    const fallback = { device_name: deviceName };
    return deviceSummary || fallback;
  }

  /**
   * { device_types, device_labels, device_sites, device_name }
   */
  @action
  getUniqueSelectedDevices(identifiers) {
    const { $sites, $labels, $assetTags } = this.store;
    const devices = [];

    if (!$sites.collection.hasFetched || !$labels.labels.hasFetched) {
      return devices;
    }

    const { device_ids, device_labels, device_assetTags, device_sites, device_types, device_name, activeDevicesOnly } =
      identifiers;

    if (Array.isArray(device_ids)) {
      devices.push(...device_ids.map((id) => this.deviceSummariesById[id]));
    }

    if (Array.isArray(device_labels)) {
      device_labels.forEach((labelId) => {
        const label = this.store.$labels.labels.get(labelId);
        if (label) {
          devices.push(
            ...label
              .get('items')
              .filter((item) => item.item_type === 'device')
              .map((item) => this.deviceSummariesById[item.item_id])
          );
        }
      });
    }

    if (Array.isArray(device_assetTags)) {
      device_assetTags.forEach((assetTagKey) => {
        const tagOption = $assetTags.deviceOptionsById[assetTagKey];

        if (Array.isArray(tagOption?.devices) && tagOption.devices.length > 0) {
          devices.push(...tagOption.devices);
        }
      });
    }

    if (Array.isArray(device_sites)) {
      device_sites.forEach((siteId) => {
        const site = this.store.$sites.collection.get(siteId);
        if (site) {
          devices.push(...site.devices);
        }
      });
    }

    if (Array.isArray(device_types)) {
      device_types.forEach((type) => devices.push(...(this.deviceSummariesByType[type] || [])));
    }

    if (Array.isArray(device_name)) {
      devices.push(...device_name.map((name) => this.getDeviceSummaryByName({ deviceName: name, activeDevicesOnly })));
    }

    // take only the uniques and get rid of garbage
    return uniqBy(
      devices.filter((device) => device),
      'device_name'
    );
  }

  filterMissingDevices(identifiers) {
    const { all_devices, device_labels, device_sites, device_types, device_name } = identifiers;
    const { $sites, $labels } = this.store;

    if (all_devices) {
      return identifiers;
    }

    return {
      all_devices,
      device_name: device_name.filter((name) => this.deviceSummariesByName[name]),
      device_types: device_types.filter(
        (type) => this.deviceSummariesByType[type] && this.deviceSummariesByType[type].length
      ),
      device_sites: device_sites.filter((site) => $sites.collection.get(site)),
      device_labels: device_labels.filter((label) => $labels.labels.get(label))
    };
  }

  getDeviceOptions(valueField = 'device_name', deviceFilter) {
    const deviceOptions = {};
    let { deviceSummaries } = this;

    if (deviceFilter) {
      deviceSummaries = deviceSummaries.filter(deviceFilter);
    }

    deviceSummaries.forEach((device) => {
      const { device_name, device_subtype, site_name } = device;
      const site = site_name || 'Unassigned';

      const deviceOption = {
        value: device[valueField],
        label: device_name,
        className: device_subtype,
        icon: deviceSubTypeIconMap[device_subtype],
        site
      };

      if (!deviceOptions[site]) {
        deviceOptions[site] = [];
      }

      deviceOptions[site].push(deviceOption);
    });

    return deviceOptions;
  }

  /**
   * Pass in a string of device_ids, get a comma separated string of device_names (for QueryModel)
   */
  getDeviceNames(ids, options = {}) {
    if (!ids) {
      return '';
    }

    const mapFn = (id) => {
      const device = this.deviceSummariesById[id];
      return device?.device_name || (options?.showUnmatched ? '<missing device>' : null);
    };

    if (Array.isArray(ids)) {
      return ids.map(mapFn).filter((device) => device);
    }

    return ids
      .split(',')
      .map(mapFn)
      .filter((device) => device);
  }

  containsKappa({ device_name, device_labels, device_assetTags, device_sites, device_types, device_ids }) {
    if (Array.isArray(device_types) && device_types.includes('kappa')) {
      return true;
    }

    return this.getUniqueSelectedDevices({
      device_ids,
      device_name,
      device_labels,
      device_assetTags,
      device_sites,
      device_types
    }).some((device) => device.device_subtype === 'kappa');
  }

  containsDnsProbe({ device_name, device_labels, device_assetTags, device_sites, device_types, device_ids }) {
    if (Array.isArray(device_types) && device_types.includes('kprobe')) {
      return true;
    }

    return this.getUniqueSelectedDevices({
      device_ids,
      device_name,
      device_labels,
      device_assetTags,
      device_sites,
      device_types
    }).some((device) => ['kprobe', 'aws_subnet', 'gcp_subnet', 'azure_subnet'].includes(device.device_subtype));
  }

  getDeviceTypeFromSubtype(device_subtype) {
    const deviceSubtypeRecord = this.store.$dictionary.dictionary.device_subtypes[device_subtype];
    return deviceSubtypeRecord ? deviceSubtypeRecord.parent_type : undefined;
  }

  getDeviceSubtypeColumns(subtype) {
    return api.get(`/api/ui/devices/subtype-col/${subtype}`);
  }

  isCloudSubtype(subtype) {
    return CLOUD_TYPE_DEVICES.includes(subtype);
  }

  isRouter(deviceId) {
    // eslint-disable-next-line eqeqeq
    const device = this.deviceSummariesById[deviceId];
    return !!(device && device.device_type === 'router');
  }

  isMetricsDevice(deviceId) {
    return !!this.collection.get(deviceId)?.get('has_nms_record');
  }

  isCloudDevice(deviceOrDeviceId) {
    const deviceId = parseInt(deviceOrDeviceId);

    if (Number.isNaN(deviceId)) {
      const device = deviceOrDeviceId || {};
      return this.isCloudSubtype(get(device.attributes || device, 'device_subtype'));
    }

    const device = this.deviceSummariesById[deviceId];
    return !!(device && device.cloud_export_id);
  }

  cloudProvider(device = {}) {
    return this.isCloudDevice(device) ? get(device.attributes || device, 'device_subtype').split('_')[0] : undefined;
  }

  bulkUpdateDeviceMonitoringTemplate(deviceIds, monitoring_template_id) {
    return api
      .post('/api/ui/devices/bulkUpdateDeviceMonitoringTemplate', { data: { deviceIds, monitoring_template_id } })
      .then(
        (monitoringTemplateId) => {
          deviceIds.forEach((deviceId) => {
            const model = this.tableCollection.get(deviceId);
            if (model && model.isNMS) {
              model.set('monitoring_template_id', monitoringTemplateId);
            }
          });
        },
        (err) => {
          console.error(err);
        }
      );
  }

  bulkUpdateDeviceNmsAgent(deviceIds, agentId) {
    return api.post('/api/ui/devices/bulkUpdateDeviceNmsAgent', { data: { deviceIds, agentId } }).then(() => {
      deviceIds.forEach((deviceId) => {
        const model = this.tableCollection.get(deviceId);
        if (model && model.isNMS) {
          model.set('device_kentik_agent_id', agentId);
        }
      });
    });
  }

  bulkUpdateDeviceSubtype(ids, subtype) {
    return api.post('/api/ui/devices/bulkUpdateDeviceSubtype', { data: { ids, subtype } }).then((response) => {
      const deviceType = this.getDeviceTypeFromSubtype(subtype);
      const updatedIds = response?.ids || [];

      deviceIndex.invalidate(updatedIds);

      updatedIds.forEach((id) => {
        const model = this.tableCollection.get(id);
        if (model) {
          model.set('device_subtype', subtype);
          if (deviceType) {
            model.set('device_type', deviceType);
          }
        }
      });

      // model.set() doesn't propagate to collection.lastUpdated, so notify observers explicitly
      this.tableCollection.setLastUpdated();

      return updatedIds;
    });
  }

  bulkUpdateCommandAccessSettings(deviceIds, updates) {
    return api.post('/api/ui/devices/bulkUpdateCommandAccessSettings', { data: { deviceIds, updates } }).then(() => {
      deviceIds.forEach((deviceId) => {
        const model = this.tableCollection.get(deviceId);
        if (model) {
          const deviceconfOptions = model.get('deviceconf_options') || {};
          if (updates.enabled !== undefined) {
            deviceconfOptions.enabled = updates.enabled;
            model.set('deviceconf_enabled', updates.enabled);
          }
          if (updates.agent_id !== undefined) {
            deviceconfOptions.agent_id = updates.agent_id;
            model.set('deviceconf_agent_id', updates.agent_id);
          }
          if (updates.credential_name !== undefined) {
            deviceconfOptions.credential_name = updates.credential_name;
            model.set('deviceconf_credential_name', updates.credential_name);
          }
          if (updates.platform !== undefined) {
            deviceconfOptions.platform = updates.platform;
            model.set('deviceconf_platform', updates.platform);
          }
          if (updates.fetch_enabled !== undefined) {
            deviceconfOptions.fetch_enabled = updates.fetch_enabled;
            model.set('deviceconf_fetch_enabled', updates.fetch_enabled);
          }
          if (updates.fetch_interval !== undefined) {
            deviceconfOptions.fetch_interval = updates.fetch_interval;
            model.set('deviceconf_fetch_interval', updates.fetch_interval);
          }
          if (updates.fetch_timeout !== undefined) {
            deviceconfOptions.fetch_timeout = updates.fetch_timeout;
            model.set('deviceconf_fetch_timeout', updates.fetch_timeout);
          }
          if (updates.command_enabled !== undefined) {
            deviceconfOptions.command_enabled = updates.command_enabled;
            model.set('deviceconf_command_enabled', updates.command_enabled);
          }
          model.set('deviceconf_options', deviceconfOptions);
        }
      });
    });
  }

  bulkUpdateCollectionMethod({
    deviceIds,
    collectionMethod,
    monitoringTemplateId,
    agentId,
    credentialName,
    snmpIpOverridesByDeviceId
  }) {
    return api.post('/api/ui/devices/bulkUpdateCollectionMethod', {
      data: { deviceIds, collectionMethod, monitoringTemplateId, agentId, credentialName, snmpIpOverridesByDeviceId }
    });
  }

  exportPdf = (search = '') => {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    this.store.$exports.fetchExport({
      path: '/api/ui/export/exportPage',
      type: 'pdf',
      fileName: `export-devices-${date}`,
      exportOptions: { location: `/v4/export/infrastructure/devices${search}` }
    });
  };

  exportCsv = (data) => {
    const path = '/api/ui/devices/csv?filterCloud=true';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fileName = `export-devices-${date}`;
    const type = 'csv';
    const options = { path, fileName, type };

    this.store.$exports.addLoadingExport(options);
    return api.post(path, { data, rawResponse: true }).then((response) => {
      this.store.$exports.clearLoadingExport(options);
      this.store.$exports.addPayload(response.text, options);
    });
  };
}

const deviceStore = new DeviceStore();

export default deviceStore;
